import { useMutation, useQuery } from '@apollo/client';
import { BrandCard } from '@seek/branding-assets';
import { useTranslations } from '@vocab/react';
import { Box, Tiles, useToast } from 'braid-design-system';
import { isEmpty } from 'lodash';
import find from 'lodash/find';
import React, { useState, useCallback, useEffect } from 'react';

import type { Brand } from 'src/App/generated/graphql-types';
import { isAsiaCover } from 'src/components/BrandEditor/utils/is-asia-cover';
import { useWithEmailVerify } from 'src/components/helpers/WithEmailVerify/withEmailVerify';
import { clearToastQueue } from 'src/gql/client-side/actions';
import { getToastQueue } from 'src/gql/client-side/queries';
import {
  SetBrandDefaultMutation,
  ClearBrandDefaultMutation,
} from 'src/gql/server-side/mutations-hg';
import { events, link } from 'src/modules/analyticsTracker';
import type { BrandListItem } from 'src/types';

import { sortBrands } from '../utils/sortBrands';

import translations from './.vocab';
import BrandControl from './Controls/BrandControl';

import * as styles from './CardGrid.css';

interface Props {
  brands: BrandListItem[];
}

const CardGrid = ({ brands }: Props) => {
  const showToast = useToast();
  const { t } = useTranslations(translations);

  const { ownerType } = useWithEmailVerify();
  const initialDefaultBrand = find(brands, ['isDefault', true]);
  const [defaultBrandId, setDefaultBrandId] = useState(initialDefaultBrand?.id);

  const { data: toasts } = useQuery(getToastQueue);
  const messages = toasts?.toastQueue;

  useEffect(() => {
    if (!isEmpty(messages)) {
      messages.forEach(showToast);
      clearToastQueue();
    }
  }, [messages, showToast]);

  const mutationOptions = {
    onError: () => {
      showToast({
        tone: 'critical',
        message: t('Something went wrong!'),
        description: t('Try again or contact customer support.'),
      });
    },
    onCompleted: (res: any) => {
      setDefaultBrandId(res?.setDefaultBrand?.id);
    },
  };

  const [setDefaultBrandMutation] = useMutation(
    SetBrandDefaultMutation,
    mutationOptions,
  );
  const [clearDefaultBrandMutation] = useMutation(
    ClearBrandDefaultMutation,
    mutationOptions,
  );

  const onToggleDefault = useCallback(
    ({ id, owner }: Brand) =>
      () => {
        const isDefault = id === defaultBrandId;
        link(
          events[
            isDefault
              ? 'CLEAR_DEFAULT_BRAND_CLICKED'
              : 'SET_DEFAULT_BRAND_CLICKED'
          ],
        );
        const changeDefaultMutation = isDefault
          ? clearDefaultBrandMutation
          : setDefaultBrandMutation;

        changeDefaultMutation({
          variables: {
            id,
            owner: {
              id: owner.id,
              type: ownerType,
            },
          },
        });
      },
    [
      ownerType,
      defaultBrandId,
      setDefaultBrandMutation,
      clearDefaultBrandMutation,
    ],
  );

  return (
    <div data-testid="card-grid">
      <Tiles columns={[1, 2]} space={['gutter', 'small']}>
        {brands
          .sort(sortBrands)
          .filter((b: BrandListItem) => b.state === 'ACTIVE')
          .map((brand: BrandListItem) => {
            const updatedBrand = {
              ...brand,
              // alternative is to use refetchQueries, however
              // if we want to migrate on new client gradually, we need to
              // manage them independently for now
              isDefault: brand.id === defaultBrandId,
            };

            const BrandControlComponent = () => (
              <BrandControl
                brand={updatedBrand}
                onToggleDefault={onToggleDefault(brand)}
              />
            );
            return (
              <Box
                data-testid="card-grid-item"
                key={brand.id}
                className={styles.item}
              >
                <BrandCard
                  // @ts-ignore
                  brand={updatedBrand}
                  BrandControl={BrandControlComponent}
                  coverPlaceholderText={t('No cover image')}
                  showNeedsUpdateBadge={isAsiaCover(brand)}
                />
              </Box>
            );
          })}
      </Tiles>
    </div>
  );
};

export default CardGrid;
